import { useQuery } from "@tanstack/react-query";
import {
  UserProfileDto,
  UserRole,
  DoubleMaterialityQuestionsControllerGetAllQuestions200Response,
  QuestionDto,
} from "@green0meter/api-client";

import { DOMAIN_DOUBLE_MATERIALITY } from "../domain";

import { roleGuard } from "@api";
import { doubleMaterialityQuestionsApi } from "@api/services";
import {
  TANSTACK_QUERY_STALE_TIME,
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
} from "@constants";


const allowedRoles = [UserRole.Manager, UserRole.Owner];
const ACTION_GET_QUESTIONS = "GET_QUESTIONS";

interface GetQuestionsRequest {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  orderBy?: string;
  orderDirection?: string;
  year: number;
}

export type QuestionItemType = QuestionDto;

interface GetQuestionsResponse {
  data: DoubleMaterialityQuestionsControllerGetAllQuestions200Response["items"];
  totalItems: number;
  pageNumber: number;
  pageSize: number;
}

const getQuestions = async (
  params: GetQuestionsRequest,
): Promise<GetQuestionsResponse> => {
  const pageSize = params?.pageSize ?? DEFAULT_PAGE_SIZE;
  const pageNumber = params?.pageNumber ?? DEFAULT_PAGE_NUMBER;

  const response =
    await doubleMaterialityQuestionsApi.doubleMaterialityQuestionsControllerGetAllQuestions(
      params.year,
      params?.search ?? null,
      params?.orderBy ?? null,
      params?.orderDirection ?? null,
      pageNumber,
      pageSize,
    );

  return {
    data: response.data.items,
    totalItems: response.data.totalCount,
    pageNumber,
    pageSize,
  };
};

export const useGetQuestions = (
  user: UserProfileDto | null,
  params: GetQuestionsRequest,
): {
  questions?: GetQuestionsResponse;
  questionsEnabled: boolean;
  isPendingGetQuestions: boolean;
  refetchQuestions: () => void;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const { isPending, data, refetch } = useQuery({
    queryKey: [DOMAIN_DOUBLE_MATERIALITY, ACTION_GET_QUESTIONS, params],
    queryFn: () => getQuestions(params),
    staleTime: TANSTACK_QUERY_STALE_TIME,
    enabled,
  });

  return {
    questions: data,
    questionsEnabled: enabled,
    isPendingGetQuestions: isPending,
    refetchQuestions: refetch,
  };
};
